import React, { useState, useEffect } from 'react';
import DashboardCard from '@/components/ui/DashboardCard';
import { useToast } from '@/hooks/errors/use-toast';
import GlassPill from '@/components/ui/GlassPill';
import { FileText } from 'lucide-react';
import { useDebouncedValue } from '@/lib/utils';
import PageGrid from '@/components/content-generator/pages/PageGrid';
import { usePrivatePages } from '@/hooks/private-text-generator/usePrivatePages';
import { transformToPageGridItem } from '@/hooks/private-text-generator/types';
import { useQueryClient } from '@tanstack/react-query';
import { useWorkbookQuery } from '@/hooks/content-generator/use-workbook-query';
import { usePageGeneration } from '@/hooks/content-generator/pages/use-page-generation';
import { useAIOperationWrapper } from '@/hooks/ai-usage/useAIOperationWrapper';
import { OpenAIThreadsService } from '@/hooks/ai-agents/services/openai/threads.service';
import { supabase } from '@/integrations/supabase/client';
import { PageGenerationRequest } from '@/types/page-generation-types';

// Import the elegant simple editor modal and new sidebar
import SimplePageEditorModal from '@/components/private-text-generator/SimplePageEditorModal';
import PrivateTextSidebar, { PageCreationFormData } from '@/components/private-text-generator/PrivateTextSidebar';
import PageCreationForm from '@/components/private-text-generator/PageCreationForm';
import PageGenerationModal from '@/components/content-generator/editor/pages/components/PageGenerationModal';

// Import the new universal empty state system
import { UniversalEmptyStateWrapper, EmptyStateLayout, getAppConfig, SimplePageForm } from '@/components/shared/empty-states';
// Import quest completion triggers
import { QuestCompletionTrigger, useQuestCompletionTrigger } from '@/components/onboarding';

const PrivateTextGeneratorContent = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const debouncedSearch = useDebouncedValue(searchQuery, 300);
  
  // Project selection state for automatic project assignment
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  
  // Document type filter state
  const [selectedDocumentType, setSelectedDocumentType] = useState<string | null>(null);
  
  // Quest completion trigger
  const { triggerPageCreated } = useQuestCompletionTrigger();
  
  // Lightbox state for page editing
  const [editingPageId, setEditingPageId] = useState<string | null>(null);

  // Listen for custom events to open page detail
  useEffect(() => {
    const handleOpenPageEvent = (event: Event) => {
      const customEvent = event as CustomEvent;
      const pageId = customEvent.detail?.pageId;
      
      console.log('[PrivateTextGenerator] Received open-page-detail event:', pageId);
      
      if (pageId) {
        console.log('[PrivateTextGenerator] Opening editor modal for page:', pageId);
        setEditingPageId(pageId);
      }
    };

    window.addEventListener('open-page-detail', handleOpenPageEvent);
    
    return () => {
      window.removeEventListener('open-page-detail', handleOpenPageEvent);
    };
  }, []);

  // Direct creation dependencies
  const { executeAIOperation } = useAIOperationWrapper();
  const pageGeneration = usePageGeneration(''); // Empty workbookId for private pages
  
  // Loading states for direct creation
  const [isCreatingSimple, setIsCreatingSimple] = useState(false);
  const [isGeneratingAdvanced, setIsGeneratingAdvanced] = useState(false);
  
  // Modal state for page generation
  const [showGenerationModal, setShowGenerationModal] = useState(false);
  const [generationRequest, setGenerationRequest] = useState<PageGenerationRequest | null>(null);

  // Fetch user pages (filtered by selected project if any)
  const {
    data: pages = [],
    isLoading,
    error,
  } = usePrivatePages(selectedProjectId);

  // Fetch ALL user pages to determine if user has any content at all (for empty state logic)
  const {
    data: allPages = [],
  } = usePrivatePages(); // No project filter = all pages

  // Get project data to show project name in header
  const { data: workbookData } = useWorkbookQuery({ 
    filterByStatus: 'all' 
  });
  const projects = workbookData?.projects || [];
  
  // Find selected project for display
  const selectedProject = selectedProjectId 
    ? projects.find(project => project.id === selectedProjectId)
    : null;

  const filteredPages = pages.filter((page) => {
    // Apply search filter
    const matchesSearch = page.title.toLowerCase().includes(debouncedSearch.toLowerCase());
    
    // Apply document type filter
    const matchesType = !selectedDocumentType || page.type === selectedDocumentType;
    
    return matchesSearch && matchesType;
  });

  const pageGridItems = filteredPages.map(transformToPageGridItem);

  // ✅ FIX: Use ALL user pages for hasData (empty state logic), not project-filtered pages
  const hasData = allPages.length > 0;

  // Get app configuration
  const appConfig = getAppConfig('private-text-generator');

  // Handle sidebar simple creation (direct DB insertion)
  const handleSidebarSimpleCreate = async (title: string, pageType?: string) => {
    console.log('[PrivateTextGenerator] Direct simple create:', { title, pageType });
    setIsCreatingSimple(true);
    
    try {
      // Get the current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('You must be logged in to create pages');
      }

      // Create dedicated OpenAI thread for this page
      console.log('[PrivateTextGenerator] Creating dedicated OpenAI thread...');
      const threadId = await OpenAIThreadsService.createOpenAIThread(executeAIOperation);
      console.log('[PrivateTextGenerator] Thread created:', threadId);

      // Direct database insertion (same logic as QuickCreatePageModal)
      const { data, error } = await supabase
        .from('workbook_page_content')
        .insert({
          workbook_id: selectedProjectId || null, // Auto-assign to selected project or private
          title: title.trim() || 'Untitled Document',
          content: '', // Empty content for blank page
          initial_request: '',
          user_id: user.id,
          openai_thread_id: threadId, // Dedicated thread for this page
          type: pageType || null
        })
        .select()
        .single();

      if (error) {
        console.error('[PrivateTextGenerator] Error creating page:', error);
        throw error;
      }
      
      // Invalidate queries to refresh the page list
      queryClient.invalidateQueries({ queryKey: ['private-pages'] });
      
      toast({
        title: "Document Created!",
        description: "Your blank document has been created successfully.",
      });
      
      // Trigger quest completion
      triggerPageCreated();
      
      // Navigate to the new page for editing
      if (data?.id) {
        setEditingPageId(data.id);
      }

      // Clear search query after successful creation
      setSearchQuery('');
      
    } catch (error) {
      console.error('[PrivateTextGenerator] Error in simple page creation:', error);
      toast({
        title: "Creation Failed",
        description: "Failed to create document. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCreatingSimple(false);
    }
  };

  // Handle sidebar advanced creation (prepare for modal)
  const handleSidebarAdvancedCreate = async (formData: PageCreationFormData) => {
    console.log('[PrivateTextGenerator] Preparing advanced create with AI:', formData);
    setIsGeneratingAdvanced(true);

    try {
      // Create dedicated OpenAI thread for this page
      console.log('[PrivateTextGenerator] Creating dedicated OpenAI thread for AI generation...');
      const threadId = await OpenAIThreadsService.createOpenAIThread(executeAIOperation);
      console.log('[PrivateTextGenerator] Thread created:', threadId);

      // Build PageGenerationRequest (same structure as NewPageModal)
      const request: PageGenerationRequest = {
        workbook_id: selectedProjectId || null, // Auto-assign to selected project or private
        title: formData.title,
        initial_request: formData.purpose || '',
        type: formData.pageType,
        writing_style: formData.writingStyle,
        goalThreadId: threadId, // Dedicated thread for this specific page
        clarifyingQuestions: [] // No clarifying questions from sidebar (simplified flow)
      };

      console.log('[PrivateTextGenerator] Opening generation modal with request:', {
        title: request.title,
        type: request.type,
        writingStyle: request.writing_style,
        threadId: request.goalThreadId
      });

      // Set the request and show the modal
      setGenerationRequest(request);
      setShowGenerationModal(true);
      
    } catch (error) {
      console.error('[PrivateTextGenerator] Error preparing page generation:', error);
      toast({
        title: "Generation Failed",
        description: "Failed to prepare document generation. Please try again.",
        variant: "destructive",
      });
      setIsGeneratingAdvanced(false);
    }
  };


  const handleEditPage = (pageId: string) => {
    setEditingPageId(pageId);
  };

  const handleCloseLightbox = () => {
    setEditingPageId(null);
  };

  const handlePageDeleted = (pageId: string) => {
    // Page was deleted, close lightbox and show success message
    setEditingPageId(null);
    
    // Invalidate private pages query to refresh the list
    queryClient.invalidateQueries({ queryKey: ['private-pages'] });
    
    toast({
      title: "Page Deleted",
      description: "The page has been deleted successfully.",
      variant: "success",
    });
  };

  // Handle generation modal close
  const handleGenerationModalClose = () => {
    setShowGenerationModal(false);
    setGenerationRequest(null);
    setIsGeneratingAdvanced(false);
    pageGeneration.reset();
  };

  // Handle generation success
  const handleGenerationSuccess = (pageId: string) => {
    console.log('[PrivateTextGenerator] Page generation successful, opening editor:', pageId);
    
    // Trigger quest completion
    triggerPageCreated();
    
    setEditingPageId(pageId);
    handleGenerationModalClose();
  };

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 dark:text-red-400">
          Failed to load pages. Please try again.
        </p>
      </div>
    );
  }

  // Sidebar component
  const sidebarContent = (
    <PrivateTextSidebar
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      onSimpleCreate={handleSidebarSimpleCreate}
      onAdvancedCreate={handleSidebarAdvancedCreate}
      isCreatingSimple={isCreatingSimple}
      isGeneratingAdvanced={isGeneratingAdvanced}
      selectedProjectId={selectedProjectId}
      onProjectChange={setSelectedProjectId}
      selectedDocumentType={selectedDocumentType}
      onDocumentTypeChange={setSelectedDocumentType}
    />
  );

  // Empty state content
  const emptyStateContent = (
    <EmptyStateLayout
      icon={<appConfig.icon className={`h-8 w-8 ${appConfig.iconColor}`} />}
      title={appConfig.emptyStateTitle}
      variant={appConfig.variant}
    >
      <SimplePageForm 
        onPageCreated={() => {
          // Page creation will automatically refresh the page list
          console.log('Private page created, layout will update automatically');
        }}
      />
    </EmptyStateLayout>
  );

  // Normal content when pages exist
  const normalContent = (
    <DashboardCard variant="purple" className="p-3 md:p-6">
      <div className="space-y-4">
        {/* Header Section (matching Private Research Generator style) */}
        <div className="flex items-center justify-between mb-3 md:mb-4">
          <div className="flex items-center gap-2">
            <FileText className="text-purple-500 dark:text-purple-400" size={18} />
            <h3 className="font-medium text-base">
              {selectedProject ? selectedProject.title : 'Your Documents'}
            </h3>
            <GlassPill 
              type={pages.length.toString()} 
              variant="purple" 
              className="ml-2 text-xs h-6 w-6 flex items-center justify-center p-0"
            />
          </div>
        </div>
        
        {/* Search and filter feedback */}
        {(debouncedSearch || selectedDocumentType) && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {debouncedSearch && selectedDocumentType ? (
              <>Showing results for "{debouncedSearch}" • {selectedDocumentType}</>
            ) : debouncedSearch ? (
              <>Showing results for "{debouncedSearch}"</>
            ) : (
              <>Showing {selectedDocumentType} documents</>
            )}
          </p>
        )}
        
        {/* Content */}
        <PageGrid 
          pages={pageGridItems}
          isLoading={isLoading}
          onPageEdit={handleEditPage}
          searchTerm={debouncedSearch || undefined}
        />
      </div>
    </DashboardCard>
  );

  return (
    <>
      {/* Quest completion trigger for page creation */}
      <QuestCompletionTrigger 
        stepId={1} 
        action="page_created_from_text_generator" 
      />
      
      <UniversalEmptyStateWrapper
        isLoading={isLoading}
        hasData={hasData}
        title={appConfig.title}
        description={appConfig.description}
        icon={<appConfig.icon size={24} />}
        sidebar={sidebarContent}
        emptyStateContent={emptyStateContent}
      >
        {normalContent}
      </UniversalEmptyStateWrapper>
      
      {/* Simple Page Editor Modal */}
      <SimplePageEditorModal
        pageId={editingPageId}
        isOpen={!!editingPageId}
        onClose={handleCloseLightbox}
        onPageDeleted={handlePageDeleted}
      />
      
      {/* Page Generation Modal - Beautiful Progress */}
      <PageGenerationModal
        isOpen={showGenerationModal}
        onClose={handleGenerationModalClose}
        requestData={generationRequest}
        selectedResearch={[]} // No research from sidebar
        summaryData={{}} // No summaries from sidebar
        onSuccess={handleGenerationSuccess}
        workbookId={undefined} // Private pages have no workbook
      />
    </>
  );
};

const PrivateTextGenerator = () => {
  return <PrivateTextGeneratorContent />;
};

export default PrivateTextGenerator; 